/**
 * BrainstormEditor — Collaborative infinite canvas with taxonomical framework zones
 * Uses React Flow for canvas + Yjs/Hocuspocus for real-time collaboration
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow, Background, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { HocuspocusProvider } from '@hocuspocus/provider';
import * as Y from 'yjs';

import ZoneNode from './brainstorm/ZoneNode';
import StickyNode from './brainstorm/StickyNode';
import TextNode from './brainstorm/TextNode';
import ShapeNode from './brainstorm/ShapeNode';
import ListView from './brainstorm/ListView';
import { FRAMEWORK_TEMPLATES, buildZoneNodes, ZONE_WIDTH, ZONE_HEIGHT } from './brainstorm/frameworkTemplates';

const API = '/api/work/documents';
const CURSOR_COLORS = ['#4f46e5', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#be185d', '#65a30d'];

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

const nodeTypes = { zone: ZoneNode, sticky: StickyNode, text: TextNode, shape: ShapeNode };

function BrainstormCanvas() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [saved, setSaved] = useState(true);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [view, setView] = useState('canvas'); // canvas | list
  const [filter, setFilter] = useState('');
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiReplace, setAiReplace] = useState(false);
  const [showExplorer, setShowExplorer] = useState(false);
  const [exploreNode, setExploreNode] = useState(null);
  const [explorePrompt, setExplorePrompt] = useState('');
  const [exploreLoading, setExploreLoading] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFileLoading, setAiFileLoading] = useState(false);
  const [aiDragOver, setAiDragOver] = useState(false);
  const aiFileRef = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const isLocalChange = useRef(false);

  // Yjs setup
  const ydoc = useMemo(() => new Y.Doc(), []);
  const yNodes = useMemo(() => ydoc.getArray('nodes'), [ydoc]);
  const yEdges = useMemo(() => ydoc.getArray('edges'), [ydoc]);
  const ySettings = useMemo(() => ydoc.getMap('settings'), [ydoc]);

  const provider = useMemo(() => {
    if (!id) return null;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return new HocuspocusProvider({
      url: `${wsProtocol}//${window.location.host}/collaboration`,
      name: `doc:${id}`,
      document: ydoc,
      token: getCookie('session_token') || 'none',
      onSynced: () => {
        setSaved(true);
        syncFromYjs();
        // Initialize framework zones if empty
        if (yNodes.length === 0) {
          const framework = ySettings.get('framework') || 'decision_making';
          const zoneNodes = buildZoneNodes(framework);
          ydoc.transact(() => {
            zoneNodes.forEach(z => yNodes.push([z]));
            ySettings.set('framework', framework);
          });
        }
      },
      onAwarenessUpdate: ({ states }) => {
        const users = [];
        states.forEach(s => { if (s.user) users.push(s.user); });
        setConnectedUsers(users);
      },
    });
  }, [id, ydoc]);

  // Sync Yjs → React Flow
  const syncFromYjs = useCallback(() => {
    if (isLocalChange.current) { isLocalChange.current = false; return; }
    const n = yNodes.toArray().map(enrichNode);
    const e = yEdges.toArray();
    setNodes(n);
    setEdges(e);
  }, [yNodes, yEdges]);

  // Store callbacks in refs to avoid circular useCallback dependencies
  const callbacksRef = useRef({});

  const enrichNode = useCallback((node) => ({
    ...node,
    data: {
      ...node.data,
      onContentChange: (id, content) => callbacksRef.current.onContentChange?.(id, content),
      onColorChange: (id, color) => callbacksRef.current.onColorChange?.(id, color),
      onTagsChange: (id, tags) => callbacksRef.current.onTagsChange?.(id, tags),
      onDelete: (id) => callbacksRef.current.onDelete?.(id),
      onDescriptionChange: (id, description) => callbacksRef.current.onDescriptionChange?.(id, description),
      onNotesChange: (id, notes) => callbacksRef.current.onNotesChange?.(id, notes),
      onAttachmentsChange: (id, attachments) => callbacksRef.current.onAttachmentsChange?.(id, attachments),
      onCommentsChange: (id, comments) => callbacksRef.current.onCommentsChange?.(id, comments),
      onAuthorsChange: (id, authors) => callbacksRef.current.onAuthorsChange?.(id, authors),
      onExplore: (id) => callbacksRef.current.onExplore?.(id),
      onStarToggle: (id) => callbacksRef.current.onStarToggle?.(id),
      onNotesOpen: (id) => callbacksRef.current.onNotesOpen?.(id),
      onNotesClose: (id) => callbacksRef.current.onNotesClose?.(id),
      _currentUser: connectedUsers[0]?.name || 'You',
    },
  }), []);

  // Observe Yjs changes from remote
  useEffect(() => {
    const observer = () => syncFromYjs();
    yNodes.observeDeep(observer);
    yEdges.observeDeep(observer);
    return () => { yNodes.unobserveDeep(observer); yEdges.unobserveDeep(observer); };
  }, [yNodes, yEdges, syncFromYjs]);

  // Load document metadata
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/${id}`, { credentials: 'include' });
        const data = await res.json();
        setDoc(data.document);
        setTitle(data.document?.title || 'Untitled Brainstorm');
      } catch { navigate(doc?.project_id ? `/work?project=${doc.project_id}` : '/work'); }
      finally { setLoading(false); }
    })();
    return () => { provider?.destroy(); ydoc.destroy(); };
  }, [id]);

  // ─── Node Operations ──────────────────────────────────

  const updateYjsNodes = useCallback((updater) => {
    isLocalChange.current = true;
    const current = yNodes.toArray();
    const updated = updater(current);
    ydoc.transact(() => {
      yNodes.delete(0, yNodes.length);
      updated.forEach(n => {
        const { data: { onContentChange, onDescriptionChange, onColorChange, onTagsChange, onDelete, onNotesChange, onAttachmentsChange, onCommentsChange, onAuthorsChange, onExplore, onStarToggle, onNotesOpen, onNotesClose, _projectId, _currentUser, ...cleanData } = {}, ...rest } = n;
        yNodes.push([{ ...rest, data: cleanData || n.data }]);
      });
    });
    // Also update React Flow state directly
    setNodes(updated.map(enrichNode));
    setSaved(false);
    clearTimeout(window._brainstormSaveTimer);
    window._brainstormSaveTimer = setTimeout(() => setSaved(true), 2000);
  }, [ydoc, yNodes, setNodes, enrichNode]);

  const updateYjsEdges = useCallback((newEdges) => {
    isLocalChange.current = true;
    ydoc.transact(() => {
      yEdges.delete(0, yEdges.length);
      newEdges.forEach(e => yEdges.push([e]));
    });
  }, [ydoc, yEdges]);

  // Handle React Flow node changes (drag, select, etc.)
  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    // Only sync position changes to Yjs
    const posChanges = changes.filter(c => c.type === 'position' && c.position);
    if (posChanges.length > 0) {
      updateYjsNodes(current => {
        const updated = [...current];
        posChanges.forEach(change => {
          const idx = updated.findIndex(n => n.id === change.id);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], position: change.position };
          }
        });
        return updated;
      });
    }
  }, [onNodesChange, updateYjsNodes]);

  // Auto-tag on drag stop: check which zone the node landed in
  const handleNodeDragStop = useCallback((event, node) => {
    if (node.type === 'zone') return;

    const zones = yNodes.toArray().filter(n => n.type === 'zone');
    const absX = node.position.x + (node.parentId ? (zones.find(z => z.id === node.parentId)?.position?.x || 0) : 0);
    const absY = node.position.y + (node.parentId ? (zones.find(z => z.id === node.parentId)?.position?.y || 0) : 0);

    let newParent = null;
    for (const zone of zones) {
      const zw = zone.style?.width || ZONE_WIDTH;
      const zh = zone.style?.height || ZONE_HEIGHT;
      if (absX >= zone.position.x && absX <= zone.position.x + zw &&
          absY >= zone.position.y && absY <= zone.position.y + zh) {
        newParent = zone;
        break;
      }
    }

    updateYjsNodes(current => current.map(n => {
      if (n.id !== node.id) return n;
      const newTags = newParent ? [newParent.data.label] : [];
      const relX = newParent ? absX - newParent.position.x : absX;
      const relY = newParent ? absY - newParent.position.y : absY;
      return {
        ...n,
        parentId: newParent?.id || undefined,
        position: { x: relX, y: relY },
        data: { ...n.data, tags: newTags },
      };
    }));
  }, [yNodes, updateYjsNodes]);

  const onConnect = useCallback((params) => {
    const newEdge = { ...params, id: `e_${Date.now()}`, type: 'smoothstep', animated: true };
    setEdges(eds => addEdge(newEdge, eds));
    updateYjsEdges([...yEdges.toArray(), newEdge]);
  }, [setEdges, yEdges, updateYjsEdges]);

  // Content editing callbacks
  const handleContentChange = useCallback((nodeId, content) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, content } } : n
    ));
  }, [updateYjsNodes]);

  const handleColorChange = useCallback((nodeId, color) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, color } } : n
    ));
  }, [updateYjsNodes]);

  const handleTagsChange = useCallback((nodeId, tags) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, tags } } : n
    ));
  }, [updateYjsNodes]);

  const handleDeleteNode = useCallback((nodeId) => {
    updateYjsNodes(current => current.filter(n => n.id !== nodeId || n.type === 'zone'));
    updateYjsEdges(yEdges.toArray().filter(e => e.source !== nodeId && e.target !== nodeId));
  }, [updateYjsNodes, updateYjsEdges, yEdges]);

  const handleDescriptionChange = useCallback((nodeId, description) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, description } } : n
    ));
  }, [updateYjsNodes]);

  const handleNotesChange = useCallback((nodeId, notes) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, notes } } : n
    ));
  }, [updateYjsNodes]);

  const handleAttachmentsChange = useCallback((nodeId, attachments) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, attachments } } : n
    ));
  }, [updateYjsNodes]);

  const handleCommentsChange = useCallback((nodeId, comments) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, comments } } : n
    ));
  }, [updateYjsNodes]);

  const handleAuthorsChange = useCallback((nodeId, authors) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, authors } } : n
    ));
  }, [updateYjsNodes]);

  const handleStarToggle = useCallback((nodeId) => {
    updateYjsNodes(current => current.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, starred: !n.data.starred } } : n
    ));
  }, [updateYjsNodes]);

  // Bring node to front when notes panel opens (React Flow z-index)
  const handleNotesOpen = useCallback((nodeId) => {
    setNodes(nds => nds.map(n => ({ ...n, zIndex: n.id === nodeId ? 1000 : undefined })));
  }, [setNodes]);

  const handleNotesClose = useCallback((nodeId) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, zIndex: undefined } : n));
  }, [setNodes]);

  // Explore a sticky note — find related ideas via AI
  const handleExplore = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    setExploreNode(node);
    setExplorePrompt('');
    setShowExplorer(true);
  }, [nodes]);

  // Keep callback ref updated
  callbacksRef.current = { onContentChange: handleContentChange, onDescriptionChange: handleDescriptionChange, onColorChange: handleColorChange, onTagsChange: handleTagsChange, onDelete: handleDeleteNode, onNotesChange: handleNotesChange, onAttachmentsChange: handleAttachmentsChange, onCommentsChange: handleCommentsChange, onAuthorsChange: handleAuthorsChange, onExplore: handleExplore, onStarToggle: handleStarToggle, onNotesOpen: handleNotesOpen, onNotesClose: handleNotesClose };

  // Add elements
  const addSticky = () => {
    const nodeId = `sticky_${Date.now()}`;
    const newNode = {
      id: nodeId, type: 'sticky',
      position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
      data: { content: '', color: '#FEF3C7', tags: [] },
    };
    updateYjsNodes(current => [...current, newNode]);
  };

  const addText = () => {
    const newNode = {
      id: `text_${Date.now()}`, type: 'text',
      position: { x: 150 + Math.random() * 200, y: 150 + Math.random() * 200 },
      data: { content: '', tags: [] },
    };
    updateYjsNodes(current => [...current, newNode]);
  };

  const addShape = (shape = 'rectangle') => {
    const newNode = {
      id: `shape_${Date.now()}`, type: 'shape',
      position: { x: 200 + Math.random() * 200, y: 200 + Math.random() * 200 },
      data: { shape, color: '#E0E7FF', tags: [] },
    };
    updateYjsNodes(current => [...current, newNode]);
  };

  const runExplore = async () => {
    if (!exploreNode || exploreLoading) return;
    setExploreLoading(true);
    try {
      const nodeData = exploreNode.data || {};
      const context = `Sticky note title: ${nodeData.content || ''}\nDescription: ${nodeData.description || ''}\nNotes: ${nodeData.notes || ''}\nTags: ${(nodeData.tags || []).join(', ')}`;
      const zoneNodes = yNodes.toArray().filter(n => n.type === 'zone');
      const zones = zoneNodes.map(z => z.data?.label).filter(Boolean);

      const prompt = `You are exploring ideas related to a sticky note in a brainstorm.

EXISTING NOTE:
${context}

USER QUESTION: ${explorePrompt || 'What options, related ideas, or considerations exist for this?'}

AVAILABLE ZONES: ${zones.join(', ') || 'none'}

Generate 3-6 NEW sticky notes that explore this idea further. Each note should be a distinct related concept, option, consideration, or action item.

Return ONLY valid JSON:
{
  "notes": [
    { "content": "short title (max 80 chars)", "description": "1-2 sentence detail", "zone": "zone name or empty", "color": "#hex" }
  ],
  "connections": [
    { "from": -1, "to": 0, "label": "relates to" }
  ]
}

Use from: -1 to connect FROM the original note. Colors: Yellow #FEF3C7 ideas, Blue #DBEAFE data, Green #D1FAE5 actions, Pink #FCE7F3 risks, Purple #E0E7FF analysis.`;

      const res = await fetch('/api/work/documents/ai-organize', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: prompt, zones: zones.map(z => ({ label: z })) }),
      });
      const data = await res.json();
      if (!res.ok || !data.notes) throw new Error(data.error || 'No results');

      // Position new notes around the source node
      const sourcePos = exploreNode.position || { x: 400, y: 300 };
      const newNodes = [];
      const newEdges = [];

      data.notes.forEach((note, i) => {
        const angle = (i / data.notes.length) * Math.PI * 2 - Math.PI / 2;
        const radius = 280;
        const nodeId = `explore_${Date.now()}_${i}`;
        const zone = zoneNodes.find(z => z.data?.label?.toLowerCase() === note.zone?.toLowerCase());

        newNodes.push({
          id: nodeId,
          type: 'sticky',
          position: {
            x: sourcePos.x + Math.cos(angle) * radius,
            y: sourcePos.y + Math.sin(angle) * radius,
          },
          parentId: zone?.id || exploreNode.parentId || undefined,
          data: {
            content: note.content,
            description: note.description || '',
            color: note.color || '#E0E7FF',
            tags: zone ? [zone.data.label] : (nodeData.tags || []),
            notes: '',
            attachments: [],
          },
        });
      });

      // Create connections
      if (data.connections) {
        data.connections.forEach((conn, i) => {
          const sourceId = conn.from === -1 ? exploreNode.id : (conn.from < newNodes.length ? newNodes[conn.from].id : null);
          const targetId = conn.to === -1 ? exploreNode.id : (conn.to < newNodes.length ? newNodes[conn.to].id : null);
          if (sourceId && targetId) {
            newEdges.push({
              id: `explore_edge_${Date.now()}_${i}`,
              source: sourceId, target: targetId,
              type: 'smoothstep', animated: true,
              label: conn.label || '',
            });
          }
        });
      }

      updateYjsNodes(current => [...current, ...newNodes]);
      if (newEdges.length) updateYjsEdges([...yEdges.toArray(), ...newEdges]);

      setShowExplorer(false);
      setExploreNode(null);
    } catch (err) {
      alert('Explore failed: ' + err.message);
    } finally {
      setExploreLoading(false);
    }
  };

  const deleteSelected = () => {
    updateYjsNodes(current => current.filter(n => !n.selected || n.type === 'zone'));
    updateYjsEdges(yEdges.toArray().filter(e => {
      const selectedIds = nodes.filter(n => n.selected).map(n => n.id);
      return !selectedIds.includes(e.source) && !selectedIds.includes(e.target);
    }));
  };

  // Extract text from a single file
  const extractFileText = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'txt' || ext === 'md') {
      return await file.text();
    }
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/work/documents/extract-text', {
      method: 'POST', credentials: 'include', body: form,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Extraction failed');
    return data.text;
  };

  // Handle file upload (input or drop) — supports multiple files
  const handleAiFiles = async (files) => {
    if (!files || files.length === 0) return;
    const validExts = ['txt', 'md', 'pdf', 'docx'];
    const validFiles = [...files].filter(f => validExts.includes(f.name.split('.').pop().toLowerCase()));
    if (validFiles.length === 0) { alert('Unsupported file type. Use PDF, DOCX, or TXT.'); return; }

    setAiFileLoading(true);
    try {
      const results = await Promise.all(validFiles.map(extractFileText));
      const combined = results.filter(Boolean).join('\n\n---\n\n');
      setAiText(prev => prev ? prev + '\n\n---\n\n' + combined : combined);
    } catch (err) {
      alert(`Could not read file: ${err.message}`);
    } finally {
      setAiFileLoading(false);
      if (aiFileRef.current) aiFileRef.current.value = '';
    }
  };

  const handleAiFile = (e) => handleAiFiles(e.target.files);

  // Drag and drop handlers
  const handleAiDrop = (e) => {
    e.preventDefault();
    setAiDragOver(false);
    handleAiFiles(e.dataTransfer.files);
  };

  // Intelligent Import: paste text → LLM creates sticky notes in zones
  const aiOrganize = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const zoneNodes = yNodes.toArray().filter(n => n.type === 'zone');
      const zones = zoneNodes.map(z => ({ label: z.data?.label, id: z.id }));

      console.log('[Intelligent Import] Sending request with', aiText.length, 'chars,', zones.length, 'zones');
      const res = await fetch('/api/work/documents/ai-organize', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: aiText, zones }),
      });
      console.log('[Intelligent Import] Response status:', res.status, 'content-type:', res.headers.get('content-type'));
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('json')) {
        const text = await res.text();
        console.error('[Intelligent Import] Non-JSON response:', text.substring(0, 200));
        throw new Error(`Server returned ${res.status} (${contentType || 'HTML'}). Try refreshing.`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
      if (!data.notes) throw new Error('No notes returned from AI');

      // Zone-aware placement: group notes per zone, lay out within zone bounds
      const STICKY_W = 200;
      const STICKY_H = 140;
      const PAD_X = 20;
      const PAD_TOP = 60; // below zone header
      const GAP_X = 10;
      const GAP_Y = 20;

      // Group notes by zone
      const notesByZone = {};
      const unmatchedNotes = [];
      data.notes.forEach(note => {
        const zone = zoneNodes.find(z => z.data?.label?.toLowerCase() === note.zone?.toLowerCase());
        if (zone) {
          (notesByZone[zone.id] = notesByZone[zone.id] || []).push({ ...note, _zone: zone });
        } else {
          unmatchedNotes.push(note);
        }
      });
      // Put unmatched notes in the first zone
      if (unmatchedNotes.length && zoneNodes.length) {
        const firstZone = zoneNodes[0];
        unmatchedNotes.forEach(n => {
          (notesByZone[firstZone.id] = notesByZone[firstZone.id] || []).push({ ...n, _zone: firstZone });
        });
      }

      // Count existing stickies per zone (for append mode)
      const existingStickyCountPerZone = {};
      if (!aiReplace) {
        const allNodes = yNodes.toArray();
        allNodes.filter(n => n.type === 'sticky').forEach(n => {
          if (n.parentId) {
            existingStickyCountPerZone[n.parentId] = (existingStickyCountPerZone[n.parentId] || 0) + 1;
          }
        });
      }

      const newNodes = [];
      const zoneHeightUpdates = {}; // track zones that need to grow

      // Lay out notes within each zone
      let globalIdx = 0;
      for (const [zoneId, notes] of Object.entries(notesByZone)) {
        const zone = zoneNodes.find(z => z.id === zoneId);
        if (!zone) continue;

        const zoneW = zone.style?.width || 560;
        const zoneH = zone.style?.height || 700;
        const cols = Math.max(1, Math.floor((zoneW - PAD_X) / (STICKY_W + GAP_X)));
        const existingCount = existingStickyCountPerZone[zoneId] || 0;

        notes.forEach((note, i) => {
          const idx = existingCount + i; // offset past existing stickies
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          const x = PAD_X + col * (STICKY_W + GAP_X);
          const y = PAD_TOP + row * (STICKY_H + GAP_Y);

          newNodes.push({
            id: `ai_${Date.now()}_${globalIdx}`,
            type: 'sticky',
            position: { x, y },
            parentId: zoneId,
            data: {
              content: note.content,
              description: note.description || '',
              color: note.color || '#FEF3C7',
              tags: zone.data?.label ? [zone.data.label] : [],
              notes: note.notes || '',
              attachments: [],
            },
          });
          globalIdx++;

          // Check if zone needs to grow
          const neededH = y + STICKY_H + GAP_Y;
          if (neededH > zoneH) {
            zoneHeightUpdates[zoneId] = Math.max(zoneHeightUpdates[zoneId] || zoneH, neededH);
          }
        });
      }

      // Create edges from connections (use original note index → globalIdx mapping)
      const newEdges = [];
      if (data.connections) {
        data.connections.forEach((conn, i) => {
          if (conn.from < newNodes.length && conn.to < newNodes.length) {
            newEdges.push({
              id: `ai_edge_${Date.now()}_${i}`,
              source: newNodes[conn.from].id,
              target: newNodes[conn.to].id,
              type: 'smoothstep',
              animated: true,
              label: conn.label || '',
            });
          }
        });
      }

      // Add to Yjs — replace or complement, and grow zones if needed
      if (aiReplace) {
        const zoneOnly = yNodes.toArray().filter(n => n.type === 'zone').map(z => {
          if (zoneHeightUpdates[z.id]) {
            return { ...z, style: { ...z.style, height: zoneHeightUpdates[z.id] } };
          }
          return z;
        });
        updateYjsNodes(() => [...zoneOnly, ...newNodes]);
        updateYjsEdges(newEdges);
      } else {
        // Update zone heights if needed
        if (Object.keys(zoneHeightUpdates).length > 0) {
          updateYjsNodes(current => current.map(n => {
            if (n.type === 'zone' && zoneHeightUpdates[n.id]) {
              return { ...n, style: { ...n.style, height: zoneHeightUpdates[n.id] } };
            }
            return n;
          }).concat(newNodes));
        } else {
          updateYjsNodes(current => [...current, ...newNodes]);
        }
        if (newEdges.length) {
          updateYjsEdges([...yEdges.toArray(), ...newEdges]);
        }
      }

      setShowAiModal(false);
      setAiText('');
      setAiReplace(false);
    } catch (err) {
      console.error('AI organize failed:', err);
      alert('AI organize failed: ' + err.message);
    } finally {
      setAiLoading(false);
    }
  };

  async function handleTitleChange(t) {
    setTitle(t);
    try {
      await fetch(`${API}/${id}`, { method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: t }) });
    } catch {}
  }

  // Filter: dim nodes that don't match
  const filteredNodes = useMemo(() => {
    if (!filter) return nodes;
    const q = filter.toLowerCase();
    return nodes.map(n => {
      if (n.type === 'zone') return n;
      const matches = n.data?.tags?.some(t => t.toLowerCase().includes(q)) ||
        n.data?.content?.toLowerCase().includes(q);
      return { ...n, style: { ...n.style, opacity: matches ? 1 : 0.15 } };
    });
  }, [nodes, filter]);

  const zoneNodes = nodes.filter(n => n.type === 'zone');

  if (loading) {
    return <div className="flex h-screen items-center justify-center bg-white">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-purple-500" />
    </div>;
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 px-3 sm:px-4 py-2 bg-white flex-shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate(doc?.project_id ? `/work?project=${doc.project_id}` : '/work')} className="rounded p-1.5 text-gray-400 hover:bg-gray-100 flex-shrink-0">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
          </div>
          <input type="text" value={title} onChange={e => handleTitleChange(e.target.value)}
            className="border-none bg-transparent text-base sm:text-lg font-semibold text-gray-900 focus:outline-none flex-1 min-w-0" placeholder="Untitled Brainstorm" />
          <span className={`text-xs flex-shrink-0 sm:hidden ${saved ? 'text-gray-400' : 'text-amber-500'}`}>{saved ? 'Saved' : 'Saving...'}</span>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5 flex-shrink-0">
            <button onClick={() => setView('canvas')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md ${view === 'canvas' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
              Canvas
            </button>
            <button onClick={() => setView('list')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md ${view === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
              List
            </button>
          </div>

          {/* Search/Filter — hidden on small screens, shown on sm+ */}
          <div className="relative hidden sm:block">
            <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder="Filter..."
              className="w-36 text-sm pl-7 pr-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-300" />
            <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>

          {/* Connected users */}
          <div className="flex -space-x-2 flex-shrink-0">
            {connectedUsers.slice(0, 3).map((u, i) => (
              <div key={i} className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 border-white text-[10px] font-bold text-white flex items-center justify-center"
                style={{ backgroundColor: u.color || CURSOR_COLORS[i % CURSOR_COLORS.length] }} title={u.name}>{(u.name||'?')[0].toUpperCase()}</div>
            ))}
          </div>
          <span className={`text-xs flex-shrink-0 hidden sm:inline ${saved ? 'text-gray-400' : 'text-amber-500'}`}>{saved ? 'Saved' : 'Saving...'}</span>
        </div>
      </header>

      {/* Toolbar */}
      {view === 'canvas' && (
        <div className="flex items-center gap-1 px-3 sm:px-4 py-1.5 bg-white border-b border-gray-100 flex-shrink-0 overflow-x-auto">
          <button onClick={addSticky} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100">
            <span className="text-sm">📝</span> Sticky
          </button>
          <button onClick={addText} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100">
            <span className="text-sm">T</span> Text
          </button>
          <button onClick={() => addShape('rectangle')} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
            <span className="text-sm">▭</span> Shape
          </button>
          <button onClick={() => addShape('circle')} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
            <span className="text-sm">○</span> Circle
          </button>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <button onClick={deleteSelected} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-500 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100">
            Delete
          </button>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <button onClick={() => setShowAiModal(true)} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100">
            <span className="text-sm">✨</span> Intelligent Import
          </button>
          <div className="flex-1" />
          <span className="text-[10px] text-gray-400">
            {nodes.filter(n => n.type !== 'zone').length} items
          </span>
        </div>
      )}

      {/* Canvas or List view */}
      {view === 'canvas' ? (
        <div className="flex-1">
          <ReactFlow
            nodes={filteredNodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeDragStop={handleNodeDragStop}
            onNodesDelete={(deleted) => {
              const ids = deleted.map(n => n.id);
              updateYjsNodes(current => current.filter(n => !ids.includes(n.id) || n.type === 'zone'));
              updateYjsEdges(yEdges.toArray().filter(e => !ids.includes(e.source) && !ids.includes(e.target)));
            }}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={[20, 20]}
            minZoom={0.1}
            maxZoom={2}
            deleteKeyCode={['Delete', 'Backspace']}
            multiSelectionKeyCode="Shift"
          >
            <Background gap={20} size={1} color="#e5e7eb" />
            <Controls position="bottom-right" />
            <MiniMap nodeStrokeWidth={3} pannable zoomable
              nodeColor={n => n.type === 'zone' ? (n.data?.color || '#ddd') : (n.data?.color || '#FEF3C7')} />
          </ReactFlow>
        </div>
      ) : (
        <ListView nodes={nodes} zones={zoneNodes} filter={filter}
          onNodeClick={(nodeId) => { setView('canvas'); /* TODO: focus node */ }} />
      )}

      {/* Intelligent Import Modal */}
      {showAiModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => !aiLoading && setShowAiModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">✨</span>
              <h3 className="text-lg font-semibold text-gray-900">Intelligent Import</h3>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Paste text, meeting notes, research, or any content — or upload a PDF, Word doc, or text file. AI will extract key ideas as sticky notes organized into your framework zones ({zoneNodes.map(z => z.data?.label).join(', ') || 'none'}).
            </p>
            <div className="relative"
              onDragOver={e => { e.preventDefault(); setAiDragOver(true); }}
              onDragLeave={() => setAiDragOver(false)}
              onDrop={handleAiDrop}>
              <textarea value={aiText} onChange={e => setAiText(e.target.value)}
                className={`w-full h-64 text-sm border-2 border-dashed rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none transition-colors ${aiDragOver ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-white'}`}
                placeholder="Paste text here or drag & drop files (PDF, DOCX, TXT)..."
                disabled={aiLoading || aiFileLoading} />
              {aiDragOver && (
                <div className="absolute inset-0 flex items-center justify-center bg-purple-50/90 rounded-lg border-2 border-dashed border-purple-400 pointer-events-none">
                  <div className="text-center">
                    <span className="text-3xl block mb-2">📄</span>
                    <span className="text-sm font-medium text-purple-600">Drop files here</span>
                  </div>
                </div>
              )}
              {aiFileLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                  <span className="text-sm text-purple-600 animate-pulse">Reading files...</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2">
              <input ref={aiFileRef} type="file" accept=".txt,.md,.pdf,.docx" multiple
                className="hidden" onChange={handleAiFile} disabled={aiLoading || aiFileLoading} />
              <button onClick={() => aiFileRef.current?.click()}
                disabled={aiLoading || aiFileLoading}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5">
                <span>📎</span> Upload files <span className="text-gray-400">(PDF, DOCX, TXT)</span>
              </button>
              {aiText.length > 0 && (
                <button onClick={() => setAiText('')} disabled={aiLoading}
                  className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
              )}
            </div>
            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
              <input type="checkbox" checked={aiReplace} onChange={e => setAiReplace(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
              <span className="text-xs text-gray-600">Replace existing notes <span className="text-gray-400">(removes all current sticky notes)</span></span>
            </label>
            <div className="flex justify-between items-center mt-3">
              <span className="text-xs text-gray-400">
                {aiText.length > 0 ? `${aiText.length.toLocaleString()} chars${aiText.length > 3000 ? ` · ${Math.ceil(aiText.length / 3000)} LLM calls` : ''}` : 'No text yet'}
              </span>
              <div className="flex gap-3">
                <button onClick={() => { setShowAiModal(false); setAiText(''); }}
                  disabled={aiLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={aiOrganize}
                  disabled={aiLoading || aiFileLoading || !aiText.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                  {aiLoading ? (
                    <><span className="animate-spin">⟳</span> Importing...</>
                  ) : (
                    <><span>✨</span> Import & organize</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Explorer Modal */}
      {showExplorer && exploreNode && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => !exploreLoading && setShowExplorer(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">🔍</span>
              <h3 className="text-lg font-semibold text-gray-900">Explore</h3>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
              <p className="text-sm font-medium text-gray-900">{exploreNode.data?.content}</p>
              {exploreNode.data?.description && <p className="text-xs text-gray-500 mt-1">{exploreNode.data.description}</p>}
              {exploreNode.data?.notes && <p className="text-xs text-gray-400 mt-1 truncate">{exploreNode.data.notes.substring(0, 100)}</p>}
            </div>
            <input
              type="text"
              value={explorePrompt}
              onChange={e => setExplorePrompt(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runExplore()}
              placeholder="What do you want to explore? (e.g. What options do I have? What are the risks?)"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 mb-4"
              autoFocus
              disabled={exploreLoading}
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowExplorer(false)} disabled={exploreLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={runExplore} disabled={exploreLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                {exploreLoading ? <><span className="animate-spin">⟳</span> Exploring...</> : <><span>🔍</span> Explore</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BrainstormEditor() {
  return (
    <ReactFlowProvider>
      <BrainstormCanvas />
    </ReactFlowProvider>
  );
}
